// Guest Entertainment & Local Discovery — Events (Ticketmaster), Movies
// (official cinema destinations) and admin-curated Local Places.
// All external destinations open through the shared ExternalDestinationViewer.
import { useState, useEffect } from 'react';
import { ScreenShell, DEFAULT_SURFACE } from './AssistantScreens';
import { tl } from './assistantText';
import {
    Music, Film, MapPin, Clock, ChevronRight, CalendarDays, Loader2, ExternalLink, UtensilsCrossed,
} from 'lucide-react';

const FILTERS = [
    { key: 'today', txKey: 'fToday' },
    { key: 'tonight', txKey: 'fTonight' },
    { key: 'weekend', txKey: 'fWeekend' },
    { key: 'week', txKey: 'fWeek' },
    { key: 'all', txKey: 'fComingUp' },
];
const CATS = [
    { key: '', txKey: 'cAll' },
    { key: 'music', txKey: 'cMusic' },
    { key: 'sports', txKey: 'cSports' },
    { key: 'theatre', txKey: 'cTheatre' },
    { key: 'family', txKey: 'cFamily' },
];
const LOCALES = { en: 'en-CA', fr: 'fr-CA', es: 'es-ES' };

const fmtDate = (date, time, lang) => {
    if (!date) return '';
    try {
        const d = new Date(`${date}T${time || '12:00:00'}`);
        const opts = { weekday: 'short', month: 'short', day: 'numeric' };
        if (time) { opts.hour = 'numeric'; opts.minute = '2-digit'; }
        return new Intl.DateTimeFormat(LOCALES[lang] || 'en-CA', opts).format(d);
    } catch { return date; }
};

const Chip = ({ active, label, onClick, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId}
        className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${active
            ? 'bg-emerald-600 border-emerald-500 text-white'
            : 'bg-white/5 border-white/15 text-slate-300 hover:bg-white/10'}`}>
        {label}
    </button>
);

const EventCard = ({ ev, tx, lang, openDest, surface }) => (
    <div className={`${surface || DEFAULT_SURFACE} rounded-2xl overflow-hidden`} data-testid={`ent-event-${ev.id}`}>
        {ev.image && <img src={ev.image} alt={ev.name} className="w-full h-32 object-cover" />}
        <div className="p-4">
            <div className="flex items-start justify-between gap-2">
                <p className="text-white font-semibold text-sm leading-snug">{ev.name}</p>
                {ev.category && (
                    <span className="shrink-0 text-[10px] font-semibold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 rounded-full px-2 py-0.5">
                        {ev.category}
                    </span>
                )}
            </div>
            <p className="text-slate-300 text-xs mt-1.5 flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5 text-emerald-400" /> {fmtDate(ev.date, ev.time, lang)}
            </p>
            {(ev.venue || ev.distance_km != null) && (
                <p className="text-slate-400 text-xs mt-1 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {ev.venue}{ev.city ? `, ${ev.city}` : ''}{ev.distance_km != null ? ` • ${ev.distance_km} km` : ''}
                </p>
            )}
            {ev.url && (
                <button type="button" onClick={() => openDest(ev.url, ev.name)}
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
                    data-testid={`ent-event-link-${ev.id}`}>
                    {tx.entTickets} <ChevronRight className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    </div>
);

const PlaceCard = ({ p, tx, lang, openDest, surface }) => {
    const name = tl(p, 'name', lang) || tx[`cat_${p.category}`] || tx.cat_other;
    const desc = tl(p, 'description', lang);
    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid={`ent-place-${p.id}`}>
            {p.image_url && (
                <img src={p.image_url} alt={name} style={{ height: `${p.image_size || 140}px` }}
                    className="w-full object-cover" />
            )}
            <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-white font-semibold text-sm">{name}</p>
                    <span className="shrink-0 text-[10px] font-semibold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 rounded-full px-2 py-0.5">
                        {tx[`cat_${p.category}`] || tx.cat_other}
                    </span>
                </div>
                {desc && <p className="text-slate-300 text-sm mt-1.5">{desc}</p>}
                {p.url && (
                    <button type="button" onClick={() => openDest(p.url, name)}
                        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-white text-sm font-medium transition-colors"
                        data-testid={`ent-place-link-${p.id}`}>
                        {tx.moreInfo} <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default function EntertainmentScreen({ cfg, tx, lang, api, cardId, token, onBack, openDest, surface, partners = [] }) {
    const sf = surface || DEFAULT_SURFACE;
    const [tab, setTab] = useState('events');
    const [filter, setFilter] = useState('week');
    const [cat, setCat] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetch(`${api}/api/assistant/${cardId}/entertainment?token=${encodeURIComponent(token)}&filter=${filter}&category=${cat}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
            .catch(() => { if (!cancelled) { setData(null); setLoading(false); } });
        return () => { cancelled = true; };
    }, [api, cardId, token, filter, cat]);

    const events = data?.events || [];
    const places = data?.places || [];
    const movies = data?.movies;
    const legacyUrl = cfg?.url;
    const showMovies = !!movies;
    const showPlaces = places.length > 0 || !!legacyUrl;
    const showPartners = partners.length > 0;

    const tabs = [
        { key: 'events', icon: <Music className="w-4 h-4" />, label: tx.entEvents, testId: 'ent-tab-events' },
        ...(showMovies ? [{ key: 'movies', icon: <Film className="w-4 h-4" />, label: tx.entMovies, testId: 'ent-tab-movies' }] : []),
        ...(showPlaces ? [{ key: 'places', icon: <MapPin className="w-4 h-4" />, label: tx.entPlaces, testId: 'ent-tab-places' }] : []),
        ...(showPartners ? [{ key: 'partners', icon: <UtensilsCrossed className="w-4 h-4" />, label: tx.entPartners, testId: 'ent-tab-partners' }] : []),
    ];

    const cinemaName = movies?.place ? (tl(movies.place, 'name', lang) || movies.fallback.name) : movies?.fallback?.name;
    const cinemaUrl = movies?.place?.url || movies?.fallback?.url;

    return (
        <ScreenShell title={tl(cfg, 'label', lang) || tx.entertainment} onBack={onBack} tx={tx} testId="assistant-ent-screen">
            <div className="flex gap-2 mb-4">
                {tabs.map((t) => (
                    <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={t.testId}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold transition-colors border ${tab === t.key
                            ? 'bg-emerald-600 border-emerald-500 text-white'
                            : 'bg-white/5 border-white/15 text-slate-300 hover:bg-white/10'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'events' && (
                <div>
                    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="ent-filters">
                        {FILTERS.map((f) => (
                            <Chip key={f.key} active={filter === f.key} label={tx[f.txKey]}
                                onClick={() => setFilter(f.key)} testId={`ent-filter-${f.key}`} />
                        ))}
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 mt-1" data-testid="ent-categories">
                        {CATS.map((c) => (
                            <Chip key={c.key || 'all'} active={cat === c.key} label={tx[c.txKey]}
                                onClick={() => setCat(c.key)} testId={`ent-cat-${c.key || 'all'}`} />
                        ))}
                    </div>
                    <div className="space-y-3 mt-3">
                        {loading && (
                            <div className="flex flex-col items-center gap-3 py-10" data-testid="ent-loading">
                                <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
                                <p className="text-slate-400 text-sm">{tx.entLoading}</p>
                            </div>
                        )}
                        {!loading && data?.events_status === 'ok' && events.length === 0 && (
                            <div className={`${sf} rounded-2xl p-6 text-center`} data-testid="ent-empty">
                                <p className="text-slate-300 text-sm">{tx.entNoEvents}</p>
                            </div>
                        )}
                        {!loading && data?.events_status !== 'ok' && (
                            <div className={`${sf} rounded-2xl p-6 text-center`} data-testid="ent-unavailable">
                                <Clock className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                                <p className="text-slate-300 text-sm">{tx.entUnavailable}</p>
                            </div>
                        )}
                        {!loading && events.map((ev) => (
                            <EventCard key={ev.id} ev={ev} tx={tx} lang={lang} openDest={openDest} surface={sf} />
                        ))}
                    </div>
                </div>
            )}

            {tab === 'movies' && movies && (
                <div className="space-y-3">
                    <div className={`${sf} rounded-2xl overflow-hidden`} data-testid="ent-movies-card">
                        {movies.place?.image_url && (
                            <img src={movies.place.image_url} alt={cinemaName}
                                style={{ height: `${movies.place.image_size || 140}px` }} className="w-full object-cover" />
                        )}
                        <div className="p-4">
                            <div className="flex items-center gap-2">
                                <Film className="w-5 h-5 text-emerald-400" />
                                <p className="text-white font-semibold text-base">{cinemaName}</p>
                            </div>
                            {movies.place && tl(movies.place, 'description', lang) && (
                                <p className="text-slate-300 text-sm mt-1.5">{tl(movies.place, 'description', lang)}</p>
                            )}
                            {!movies.place && <p className="text-slate-400 text-xs mt-1.5">{tx.entMoviesSub}</p>}
                            {cinemaUrl && (
                                <button type="button" onClick={() => openDest(cinemaUrl, cinemaName)}
                                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
                                    data-testid="ent-movies-link">
                                    {tx.entShowtimes} <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {tab === 'places' && (
                <div className="space-y-3" data-testid="ent-places-list">
                    {places.map((p) => <PlaceCard key={p.id} p={p} tx={tx} lang={lang} openDest={openDest} surface={sf} />)}
                    {legacyUrl && (
                        <div className={`${sf} rounded-2xl p-4`} data-testid="ent-legacy-link">
                            <p className="text-white font-semibold text-sm">{tl(cfg, 'label', lang) || tx.entertainment}</p>
                            <button type="button" onClick={() => openDest(legacyUrl, tl(cfg, 'label', lang) || tx.entertainment)}
                                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-white text-sm font-medium transition-colors"
                                data-testid="ent-legacy-link-btn">
                                {tx.moreInfo} <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                </div>
            )}
            {tab === 'partners' && (
                <div className="space-y-3" data-testid="ent-partners-list">
                    <p className="text-slate-400 text-xs mb-1" data-testid="ent-partners-note">{tx.entPartnersNote}</p>
                    {partners.map((p) => (
                        <div key={p.id || p.name} className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={`ent-partner-${p.id || p.name}`}>
                            <div className="flex items-center gap-2">
                                <UtensilsCrossed className="w-4 h-4 text-emerald-400 shrink-0" />
                                <p className="text-white font-semibold text-sm">{p.name}</p>
                            </div>
                            <button type="button" onClick={() => openDest(p.url, p.name)}
                                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-white text-sm font-medium transition-colors"
                                data-testid={`ent-partner-link-${p.id || p.name}`}>
                                {tx.entPartnerVisit} <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </ScreenShell>
    );
}
